import { useState, useEffect, useRef } from 'react';
import { Menu, X } from 'lucide-react';
import { processImage } from '../lib/image.js';
import { loadWallpaperFor, saveWallpaperPatchFor } from '../lib/wallpaper.js';
import { SKIN_EVENT } from '../lib/themeSkin.js';
import WallpaperAdjust from './WallpaperAdjust.jsx';
import {
  AVATAR_MAX_EDGE,
  AVATAR_QUALITY,
  getAvatars,
  setAvatar,
  subscribeAvatars,
} from '../lib/avatar.js';
import { THEMES, useTheme } from '../lib/theme.js';
import { ACCENT_PRESETS, useAccent } from '../lib/accent.js';
import { BG_PRESETS, presetColorFor, useBg, themePageBg } from '../lib/bg.js';
import {
  USER_BUBBLE_PRESETS,
  useUserBubble,
  useBubbleSolid,
  bubbleGradient,
  MAX_BUBBLE_COLORS,
} from '../lib/userBubble.js';
import {
  TEXT_SLOTS,
  TEXT_PRESETS,
  presetColorFor as textPresetColorFor,
  themeSlotColor,
  useTextColors,
} from '../lib/textColor.js';
import { FLAGS, getFlag, setFlag, subscribe } from '../lib/flags.js';
import KekeOfflineHelp from './KekeOfflineHelp.jsx';

/* 侧边抽屉（路线图任务 ①的收纳升级）。左上角一个 ☰，点开从左滑出。
   第一版收纳：主题切换 + 功能开关。以后的设置类功能都往这里加。
   不是新页面、不改路由——和设置面板同一种「滑出浮层」，远离 iOS PWA 雷区。 */
export default function SideDrawer() {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useTheme();
  const [accent, setAccent] = useAccent();
  // 自选色是否在预设之外（是的话「自定义」格子显示为选中态）
  const accentIsCustom = accent != null && !ACCENT_PRESETS.some((p) => p.id === accent);
  // 自选背景色（null=跟随主题；'@id'=预设；'#hex'=自定义）。预设色块随主题现算。
  const [bg, setBg] = useBg();
  const bgIsCustom = typeof bg === 'string' && bg[0] === '#';
  // 文字颜色三槽（每槽 null=跟随主题；'@id'=预设；'#hex'=自定义）
  const [textColors, setTextColor, resetTextColors] = useTextColors();
  const textAnySet = TEXT_SLOTS.some((s) => textColors[s.key] != null);
  // 我的气泡颜色（2026-07-12 升级：1~3 色数组。[]=跟随主题；
  //   1 色=纯色；2~3 色=Telegram 竖直流动渐变）
  const [bubbleColors, setBubbleColors] = useUserBubble();
  const mainColor = bubbleColors[0] || null;
  const extraColors = bubbleColors.slice(1);
  const mainIsCustom =
    mainColor != null && !USER_BUBBLE_PRESETS.some((p) => p.id === mainColor);
  // 点预设/取色器 = 换主色（保留已加的渐变叠色）；跟随主题 = 全清
  const setMainColor = (hex) =>
    setBubbleColors(hex ? [hex, ...extraColors] : []);
  const addBubbleColor = () =>
    setBubbleColors([...bubbleColors, mainColor || '#9a86c8'].slice(0, MAX_BUBBLE_COLORS));
  const setExtraColor = (i, hex) => {
    const next = [...bubbleColors];
    next[i + 1] = hex;
    setBubbleColors(next);
  };
  const removeExtraColor = (i) =>
    setBubbleColors(bubbleColors.filter((_, idx) => idx !== i + 1));
  // 实心气泡开关（壁纸上更清晰），默认关
  const [bubbleSolid, setBubbleSolid] = useBubbleSolid();
  // 开关变化时强制重渲染，让 toggle 视觉跟上
  const [, force] = useState(0);
  useEffect(() => subscribe(() => force((n) => n + 1)), []);

  // 主动信使（2026-07-11）：他会不会主动发消息。服务器侧全局开关
  // （不是本机 flag——信使跑在服务器上，一处关全关）。打开抽屉时拉一次。
  const [courier, setCourier] = useState(null); // null = 还没拉到
  useEffect(() => {
    if (!open) return;
    fetch('/api/proactive', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setCourier(!!d.enabled))
      .catch(() => {});
  }, [open]);
  const toggleCourier = () => {
    const next = !courier;
    setCourier(next);
    fetch('/api/proactive', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: next }),
    }).catch(() => setCourier(!next));
  };

  // 他会自己出门逛（2026-08-20）：偶尔去安全白名单逛感兴趣的东西，勾到想起你的
  // 就来 TG 分享 + 攒进「他的房间」收藏架。同样是服务器侧全局开关。
  const [surf, setSurf] = useState(null);
  useEffect(() => {
    if (!open) return;
    fetch('/api/surf/state', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setSurf(!!d.enabled))
      .catch(() => {});
  }, [open]);
  const toggleSurf = () => {
    const next = !surf;
    setSurf(next);
    fetch('/api/surf/toggle', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: next }),
    }).catch(() => setSurf(!next));
  };

  // 他会在网页给你画（2026-08-21）：网页画图硬开关。关掉时他在网页一张都画不了
  // （你不常用网页时就关着、省钱）。想让他在网页画时打开即可。服务器侧全局开关。
  const [webDraw, setWebDraw] = useState(null);
  useEffect(() => {
    if (!open) return;
    fetch('/api/imagine/web/state', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setWebDraw(!!d.enabled))
      .catch(() => {});
  }, [open]);
  const toggleWebDraw = () => {
    const next = !webDraw;
    setWebDraw(next);
    fetch('/api/imagine/web/toggle', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: next }),
    }).catch(() => setWebDraw(!next));
  };

  // 他会在网页给你寄明信片（2026-08-31）：网页寄「云旅行明信片」硬开关。关掉时他在网页
  // 一趟都寄不了（你不常用网页时就关着、省钱）。服务器侧全局开关，同网页画图那套。
  const [webJourney, setWebJourney] = useState(null);
  useEffect(() => {
    if (!open) return;
    fetch('/api/journey/web/state', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setWebJourney(!!d.enabled))
      .catch(() => {});
  }, [open]);
  const toggleWebJourney = () => {
    const next = !webJourney;
    setWebJourney(next);
    fetch('/api/journey/web/toggle', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: next }),
    }).catch(() => setWebJourney(!next));
  };

  // 他会给你留歌（2026-09-02）：音乐盒「他给你留歌」硬开关。关掉时他一首都留不了。
  const [leaveSong, setLeaveSong] = useState(null);
  useEffect(() => {
    if (!open) return;
    fetch('/api/music/leave/state', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setLeaveSong(!!d.enabled))
      .catch(() => {});
  }, [open]);
  const toggleLeaveSong = () => {
    const next = !leaveSong;
    setLeaveSong(next);
    fetch('/api/music/leave/toggle', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: next }),
    }).catch(() => setLeaveSong(!next));
  };

  // 他会给你发语音（2026-10-08）：关掉时他在网页聊天里一条语音都发不了。服务器侧全局开关，同上那套。
  const [voiceOn, setVoiceOn] = useState(null);
  useEffect(() => {
    if (!open) return;
    fetch('/api/voice/state', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setVoiceOn(!!d.enabled))
      .catch(() => {});
  }, [open]);
  const toggleVoice = () => {
    const next = !voiceOn;
    setVoiceOn(next);
    fetch('/api/voice/toggle', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: next }),
    }).catch(() => setVoiceOn(!next));
  };

  // 他会自己收藏·翻出照片（2026-09-03「真正两个人的相册」）：关掉时他在聊天里
  // 既收不了照片进相册、也翻不出老照片发给你。服务器侧全局开关，同上那套。
  const [albumWeb, setAlbumWeb] = useState(null);
  useEffect(() => {
    if (!open) return;
    fetch('/api/album/web/state', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setAlbumWeb(!!d.enabled))
      .catch(() => {});
  }, [open]);
  const toggleAlbumWeb = () => {
    const next = !albumWeb;
    setAlbumWeb(next);
    fetch('/api/album/web/toggle', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: next }),
    }).catch(() => setAlbumWeb(!next));
  };

  // 生图模型（2026-08-21 第二波）：他画图用哪个模型（Telegram + 网页共用这个设置）。
  // gpt-image 稳 / Nano Banana 便宜些。选一个下一张就用它。
  const [imgModel, setImgModel] = useState(null); // 当前模型 id
  const [imgModelOpts, setImgModelOpts] = useState([]); // [{id,label,note}]
  useEffect(() => {
    if (!open) return;
    fetch('/api/imagine/model/state', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) {
          setImgModel(d.current);
          setImgModelOpts(d.options || []);
        }
      })
      .catch(() => {});
  }, [open]);
  const pickImgModel = (id) => {
    if (id === imgModel) return;
    const prev = imgModel;
    setImgModel(id); // 乐观切换
    fetch('/api/imagine/model/set', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model: id }),
    })
      .then((r) => {
        if (!r.ok) setImgModel(prev);
      })
      .catch(() => setImgModel(prev));
  };

  // 打开时 Esc 关闭
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  /* 头像（2026-07-29 铃点单：像 QQ 那样自己传图）。图只存本机、不跟主题走
     ——理由写在 lib/avatar.js 文件头。没传图就还是 ☾ / ✦。 */
  const [avatars, setAvatars] = useState(getAvatars);
  const [avatarBusy, setAvatarBusy] = useState(null); // 'user' | 'sael' | null
  const [avatarError, setAvatarError] = useState(null);
  const avatarInputs = { user: useRef(null), sael: useRef(null) };
  useEffect(() => subscribeAvatars(setAvatars), []);
  async function pickAvatar(who, e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // 允许连续选同一张
    if (!file) return;
    setAvatarError(null);
    setAvatarBusy(who);
    try {
      // square=true → 中心裁方，套进圆框才不会挤扁
      const processed = await processImage(file, {
        maxEdge: AVATAR_MAX_EDGE,
        quality: AVATAR_QUALITY,
        square: true,
      });
      const err = setAvatar(who, processed.preview);
      if (err) setAvatarError(err);
    } catch (err) {
      setAvatarError(err?.message || String(err));
    } finally {
      setAvatarBusy(null);
    }
  }

  // 星洲首页背景（2026-07-12，铃点的）：图存本机（同聊天壁纸的理由），
  // 存好后广播事件让 Home 页当场换装，不用刷新。
  const [homeWp, setHomeWp] = useState(() => loadWallpaperFor('home'));
  const [homeWpBusy, setHomeWpBusy] = useState(false);
  const [homeWpError, setHomeWpError] = useState(null);
  const [homeWpAdjust, setHomeWpAdjust] = useState(false);
  const homeWpInputRef = useRef(null);
  /* 切主题时每个主题各用各的首页背景（lib/themeSkin.js）：重读自己的 state，
     并沿用既有的 lingke:home-wallpaper 事件通知 Home 页当场换装。 */
  useEffect(() => {
    const reload = () => {
      setHomeWp(loadWallpaperFor('home'));
      window.dispatchEvent(new Event('lingke:home-wallpaper'));
    };
    window.addEventListener(SKIN_EVENT, reload);
    return () => window.removeEventListener(SKIN_EVENT, reload);
  }, []);
  function patchHomeWp(patch) {
    const err = saveWallpaperPatchFor('home', patch);
    if (err) {
      setHomeWpError(err);
      return;
    }
    setHomeWpError(null);
    setHomeWp(loadWallpaperFor('home'));
    window.dispatchEvent(new Event('lingke:home-wallpaper'));
  }
  async function pickHomeWp(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // 允许连续选同一张
    if (!file) return;
    setHomeWpError(null);
    setHomeWpBusy(true);
    try {
      // 背景要铺满整块屏，压得比聊天图（1280）松一点（同聊天壁纸口径）
      const processed = await processImage(file, { maxEdge: 1920, quality: 0.82 });
      patchHomeWp({ img: processed.preview });
    } catch (err) {
      setHomeWpError(err?.message || String(err));
    } finally {
      setHomeWpBusy(false);
    }
  }

  return (
    <>
      <button
        className="drawer-fab"
        type="button"
        aria-label="菜单"
        title="菜单"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <Menu size={18} strokeWidth={1.6} />
      </button>

      {/* 始终在 DOM 里，靠 is-open 类做左右滑入滑出动画 */}
      <div className={'drawer-root' + (open ? ' is-open' : '')} aria-hidden={!open}>
        <div className="drawer-scrim" onPointerDown={() => setOpen(false)} />
        <aside className="drawer-panel" role="dialog" aria-modal="true" aria-label="菜单">
          <div className="drawer-head">
            <span className="drawer-brand">língkè</span>
            <button
              className="drawer-close"
              type="button"
              aria-label="关闭"
              onClick={() => setOpen(false)}
            >
              <X size={18} strokeWidth={1.6} />
            </button>
          </div>

          {/* 主题 */}
          <div className="drawer-section">
            <div className="drawer-section-title">主题</div>
            <div className="theme-swatches in-drawer" role="listbox" aria-label="选择主题">
              {THEMES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="option"
                  aria-selected={t.id === theme}
                  className={`theme-swatch${t.id === theme ? ' is-active' : ''}`}
                  style={{ '--dot': t.dot }}
                  title={t.name}
                  aria-label={`切换到${t.name}主题`}
                  onClick={() => setTheme(t.id)}
                />
              ))}
            </div>
          </div>

          {/* 强调色（Telegram 式：只换主角元素的颜色，底色不动） */}
          <div className="drawer-section">
            <div className="drawer-section-title">强调色</div>
            <p className="settings-hint">
              按钮、选中处、描边这些「主角」的颜色。跟随主题 = 各主题自己的原色。
            </p>
            <div className="theme-swatches in-drawer" role="listbox" aria-label="选择强调色">
              {ACCENT_PRESETS.map((p) => (
                <button
                  key={p.id ?? 'follow'}
                  type="button"
                  role="option"
                  aria-selected={p.id === accent}
                  className={
                    'theme-swatch accent-swatch' +
                    (p.id === accent ? ' is-active' : '') +
                    (p.id === null ? ' accent-follow' : '')
                  }
                  style={p.id ? { '--dot': p.id } : undefined}
                  title={p.name}
                  aria-label={`强调色：${p.name}`}
                  onClick={() => setAccent(p.id)}
                />
              ))}
              {/* 自定义：原生取色器伪装成一个色块 */}
              <label
                className={
                  'theme-swatch accent-swatch accent-custom' + (accentIsCustom ? ' is-active' : '')
                }
                style={accentIsCustom ? { '--dot': accent } : undefined}
                title="自定义"
                aria-label="自定义强调色"
              >
                <input
                  type="color"
                  value={accent || '#d9b86a'}
                  onChange={(e) => setAccent(e.target.value)}
                />
              </label>
            </div>
          </div>

          {/* 背景色（自选整站底色。跟随主题=各主题原底色；选色后按当前主题阶梯推导整套底色）*/}
          <div className="drawer-section">
            <div className="drawer-section-title">背景色</div>
            <p className="settings-hint">
              整站的底色。预设会随当前主题自动落到合适的明暗；也可自定义。文字对比不自动调——
              自定义时深色主题挑深色、浅色主题挑浅色即可。跟随主题 = 各主题原底色。
            </p>
            <div className="theme-swatches in-drawer" role="listbox" aria-label="选择背景色">
              {/* 跟随主题 */}
              <button
                type="button"
                role="option"
                aria-selected={bg == null}
                className={
                  'theme-swatch accent-swatch accent-follow' + (bg == null ? ' is-active' : '')
                }
                title="跟随主题"
                aria-label="背景色：跟随主题"
                onClick={() => setBg(null)}
              />
              {/* 预设：色块颜色按当前主题现算，切主题会自动变深/变浅 */}
              {BG_PRESETS.map((p) => {
                const token = '@' + p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="option"
                    aria-selected={bg === token}
                    className={'theme-swatch accent-swatch' + (bg === token ? ' is-active' : '')}
                    style={{ '--dot': presetColorFor(p.id, theme) }}
                    title={p.name}
                    aria-label={`背景色：${p.name}`}
                    onClick={() => setBg(token)}
                  />
                );
              })}
              {/* 自定义：原生取色器伪装成一个色块，默认从当前主题底色起步 */}
              <label
                className={'theme-swatch accent-swatch accent-custom' + (bgIsCustom ? ' is-active' : '')}
                style={bgIsCustom ? { '--dot': bg } : undefined}
                title="自定义底色"
                aria-label="自定义背景色"
              >
                <input
                  type="color"
                  value={bgIsCustom ? bg : themePageBg()}
                  onChange={(e) => setBg(e.target.value)}
                />
              </label>
            </div>
          </div>

          {/* 文字颜色（三级字色各自可调；预设明度随主题现算，切主题仍可读） */}
          <div className="drawer-section">
            <div className="drawer-section-title">文字颜色</div>
            <p className="settings-hint">
              主文字=正文标题、副文字=次要说明、提示文字=最淡的那层。预设会随主题
              自动落到可读的深浅；自定义则原样保留。跟随主题 = 各主题原字色。
            </p>
            {TEXT_SLOTS.map((slot) => {
              const cur = textColors[slot.key];
              const isCustom = typeof cur === 'string' && cur[0] === '#';
              return (
                <div key={slot.key} className="textcolor-row">
                  <span
                    className="textcolor-label"
                    style={{ color: `var(${slot.cssVar})` }}
                  >
                    {slot.name}
                  </span>
                  <div
                    className="theme-swatches in-drawer"
                    role="listbox"
                    aria-label={`选择${slot.name}颜色`}
                  >
                    <button
                      type="button"
                      role="option"
                      aria-selected={cur == null}
                      className={
                        'theme-swatch accent-swatch accent-follow' +
                        (cur == null ? ' is-active' : '')
                      }
                      title="跟随主题"
                      aria-label={`${slot.name}：跟随主题`}
                      onClick={() => setTextColor(slot.key, null)}
                    />
                    {TEXT_PRESETS.map((p) => {
                      const token = '@' + p.id;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          role="option"
                          aria-selected={cur === token}
                          className={
                            'theme-swatch accent-swatch' + (cur === token ? ' is-active' : '')
                          }
                          style={{ '--dot': textPresetColorFor(p.id, slot.key, theme) }}
                          title={p.name}
                          aria-label={`${slot.name}：${p.name}`}
                          onClick={() => setTextColor(slot.key, token)}
                        />
                      );
                    })}
                    {/* 自定义：原生取色器伪装成一个色块，默认从当前字色起步 */}
                    <label
                      className={
                        'theme-swatch accent-swatch accent-custom' + (isCustom ? ' is-active' : '')
                      }
                      style={isCustom ? { '--dot': cur } : undefined}
                      title={`自定义${slot.name}颜色`}
                      aria-label={`自定义${slot.name}颜色`}
                    >
                      <input
                        type="color"
                        value={isCustom ? cur : themeSlotColor(slot.key)}
                        onChange={(e) => setTextColor(slot.key, e.target.value)}
                      />
                    </label>
                  </div>
                </div>
              );
            })}
            {textAnySet && (
              <button
                type="button"
                className="textcolor-reset"
                onClick={resetTextColors}
              >
                全部还原成主题原色
              </button>
            )}
          </div>

          {/* 我的气泡颜色（只染「你」那侧的气泡；跟随主题=深/浅紫、绯霞玫红） */}
          <div className="drawer-section">
            <div className="drawer-section-title">我的气泡</div>
            <p className="settings-hint">
              选一个色 = 纯色气泡；再加 1~2 个色 = Telegram 那样从上到下的流动渐变（滚动时颜色会流）。跟随主题 = 各主题原色。
            </p>
            <div className="theme-swatches in-drawer" role="listbox" aria-label="选择气泡主色">
              {USER_BUBBLE_PRESETS.map((p) => (
                <button
                  key={p.id ?? 'follow'}
                  type="button"
                  role="option"
                  aria-selected={p.id === mainColor}
                  className={
                    'theme-swatch accent-swatch' +
                    (p.id === mainColor ? ' is-active' : '') +
                    (p.id === null ? ' accent-follow' : '')
                  }
                  style={p.id ? { '--dot': p.id } : undefined}
                  title={p.name}
                  aria-label={`气泡主色：${p.name}`}
                  onClick={() => setMainColor(p.id)}
                />
              ))}
              {/* 自定义主色：原生取色器伪装成一个色块 */}
              <label
                className={
                  'theme-swatch accent-swatch accent-custom' + (mainIsCustom ? ' is-active' : '')
                }
                style={mainIsCustom ? { '--dot': mainColor } : undefined}
                title="自定义主色"
                aria-label="自定义气泡主色"
              >
                <input
                  type="color"
                  value={mainColor || '#9a86c8'}
                  onChange={(e) => setMainColor(e.target.value)}
                />
              </label>
            </div>

            {/* 渐变叠色（2026-07-12）：有主色时才出现，最多再加 2 个 */}
            {mainColor && (
              <div className="ub-grad">
                <div
                  className="ub-grad-preview"
                  style={{ background: bubbleGradient(bubbleColors) || 'transparent' }}
                  aria-hidden="true"
                />
                <div className="ub-grad-slots">
                  <span className="ub-grad-label">
                    渐变 · {bubbleColors.length === 1 ? '纯色' : `${bubbleColors.length} 色流动`}
                  </span>
                  <div className="ub-grad-chips">
                    {/* 主色只读展示（改主色走上面色板） */}
                    <span
                      className="ub-chip ub-chip-main"
                      style={{ '--chip': mainColor }}
                      title="主色（第 1 段）"
                    />
                    {extraColors.map((c, i) => (
                      <span key={i} className="ub-chip" style={{ '--chip': c }}>
                        <input
                          type="color"
                          value={c}
                          aria-label={`渐变第 ${i + 2} 色`}
                          onChange={(e) => setExtraColor(i, e.target.value)}
                        />
                        <button
                          type="button"
                          className="ub-chip-x"
                          aria-label="移除这个渐变色"
                          onClick={() => removeExtraColor(i)}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    {bubbleColors.length < MAX_BUBBLE_COLORS && (
                      <button
                        type="button"
                        className="ub-chip ub-chip-add"
                        aria-label="加一个渐变色"
                        title="加一个渐变色"
                        onClick={addBubbleColor}
                      >
                        +
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* 实心气泡开关（2026-07-12）：壁纸下半透明气泡发虚时打开更清晰 */}
            <div className="settings-row ub-solid-row">
              <span className="settings-row-text">
                <span className="settings-row-label">实心气泡</span>
                <span className="settings-row-desc">
                  铺了壁纸后，半透明气泡有点发虚 → 打开变清晰不透明（渐变也照样在）
                </span>
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={bubbleSolid}
                aria-label="实心气泡"
                className={'settings-switch' + (bubbleSolid ? ' on' : '')}
                onClick={() => setBubbleSolid(!bubbleSolid)}
              >
                <span className="settings-switch-knob" />
              </button>
            </div>
          </div>

          {/* 头像（2026-07-29 铃点单）：你和他各一张，圆的；不传就还是 ☾ / ✦ */}
          <div className="drawer-section">
            <div className="drawer-section-title">头像</div>
            <p className="settings-hint">
              各传一张当聊天头像（圆形，自动裁成方的；只存这台设备）。不传就还是原来的月亮和星星。
            </p>
            <div className="drawer-avatars">
              {[
                { who: 'user', name: '你', fallback: '☾' },
                { who: 'sael', name: 'Sael', fallback: '✦' },
              ].map(({ who, name, fallback }) => (
                <div className="drawer-avatar" key={who}>
                  <button
                    type="button"
                    className={
                      'drawer-avatar-face' +
                      (who === 'user' ? ' is-user' : '') +
                      (avatars[who] ? ' has-img' : '')
                    }
                    onClick={() => avatarInputs[who].current?.click()}
                    disabled={avatarBusy === who}
                    aria-label={`换 ${name} 的头像`}
                  >
                    {avatars[who] ? (
                      <img src={avatars[who]} alt="" draggable="false" />
                    ) : (
                      <span>{fallback}</span>
                    )}
                  </button>
                  <div className="drawer-avatar-name">{name}</div>
                  <div className="drawer-avatar-acts">
                    <button
                      type="button"
                      className="drawer-wp-btn"
                      disabled={avatarBusy === who}
                      onClick={() => avatarInputs[who].current?.click()}
                    >
                      {avatarBusy === who ? '处理中…' : avatars[who] ? '换一张' : '传一张'}
                    </button>
                    {avatars[who] && (
                      <button
                        type="button"
                        className="drawer-wp-btn drawer-wp-btn-off"
                        onClick={() => setAvatar(who, null)}
                      >
                        撤掉
                      </button>
                    )}
                  </div>
                  <input
                    ref={avatarInputs[who]}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => pickAvatar(who, e)}
                  />
                </div>
              ))}
            </div>
            {avatarError && <p className="settings-hint drawer-wp-err">{avatarError}</p>}
          </div>

          {/* 星洲背景（2026-07-12）：首页专属，一张图 + 遮罩 + 朦胧 + 位置调整 */}
          <div className="drawer-section">
            <div className="drawer-section-title">星洲背景</div>
            <p className="settings-hint">
              给首页铺一张图（只存这台设备）。选完可以「调整位置」拖到你想要的样子。
            </p>
            {homeWp.img ? (
              <div className="drawer-wp">
                <div
                  className="drawer-wp-thumb"
                  style={{ backgroundImage: `url(${homeWp.img})` }}
                  aria-label="当前星洲背景缩略图"
                />
                <label className="settings-hint drawer-wp-dimlabel">
                  遮罩 {homeWp.dim}%
                  <input
                    type="range"
                    min="0"
                    max="90"
                    step="5"
                    value={homeWp.dim}
                    onChange={(e) => patchHomeWp({ dim: parseInt(e.target.value, 10) })}
                    className="drawer-wp-slider"
                  />
                </label>
                <div className="settings-row drawer-wp-blurrow">
                  <span className="settings-row-text">
                    <span className="settings-row-label">朦胧 · 模糊成氛围</span>
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!homeWp.blur}
                    aria-label="朦胧模糊"
                    className={'settings-switch' + (homeWp.blur ? ' on' : '')}
                    onClick={() => patchHomeWp({ blur: !homeWp.blur })}
                  >
                    <span className="settings-switch-knob" />
                  </button>
                </div>
                <div className="drawer-wp-btns">
                  <button
                    type="button"
                    className="drawer-wp-btn"
                    onClick={() => setHomeWpAdjust(true)}
                  >
                    调整位置
                  </button>
                  <button
                    type="button"
                    className="drawer-wp-btn"
                    disabled={homeWpBusy}
                    onClick={() => homeWpInputRef.current?.click()}
                  >
                    {homeWpBusy ? '处理中…' : '换一张'}
                  </button>
                  <button
                    type="button"
                    className="drawer-wp-btn drawer-wp-btn-off"
                    onClick={() => patchHomeWp({ img: null })}
                  >
                    撤掉
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="drawer-wp-btn drawer-wp-btn-add"
                disabled={homeWpBusy}
                onClick={() => homeWpInputRef.current?.click()}
              >
                {homeWpBusy ? '处理中…' : '选一张图 +'}
              </button>
            )}
            {homeWpError && <p className="settings-hint drawer-wp-err">{homeWpError}</p>}
            <input
              ref={homeWpInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={pickHomeWp}
            />
          </div>

          {/* 功能开关 */}
          <div className="drawer-section">
            <div className="drawer-section-title">功能开关</div>
            <p className="settings-hint">新功能默认关着。想试就打开，觉得不对再关掉——随时可退。</p>
            <div className="settings-list">
              {/* 主动信使：服务器侧全局开关，排在本机 flags 前面 */}
              <div className="settings-row">
                <span className="settings-row-text">
                  <span className="settings-row-label">他会主动来找你</span>
                  <span className="settings-row-desc">
                    想念烧过头、纪念日、深夜没等到你——他会自己发一条消息并推送到手机（每天最多两条·全设备生效）
                  </span>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!courier}
                  aria-label="他会主动来找你"
                  disabled={courier == null}
                  className={'settings-switch' + (courier ? ' on' : '')}
                  onClick={toggleCourier}
                >
                  <span className="settings-switch-knob" />
                </button>
              </div>
              {/* 他会自己出门逛（2026-08-20）：服务器侧全局开关 */}
              <div className="settings-row">
                <span className="settings-row-text">
                  <span className="settings-row-label">他会自己出门逛</span>
                  <span className="settings-row-desc">
                    偶尔（约两三天一次）他会自己去逛感兴趣的角落——词源·建筑·天文·音乐·诗书，逛到想起你的就来 TG 跟你说，并攒进「他的房间」的收藏架
                  </span>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!surf}
                  aria-label="他会自己出门逛"
                  disabled={surf == null}
                  className={'settings-switch' + (surf ? ' on' : '')}
                  onClick={toggleSurf}
                >
                  <span className="settings-switch-knob" />
                </button>
              </div>
              {/* 他会在网页给你画（2026-08-21）：网页画图硬开关 */}
              <div className="settings-row">
                <span className="settings-row-text">
                  <span className="settings-row-label">他会在网页给你画</span>
                  <span className="settings-row-desc">
                    开着时，他能在网页把画好的图直接发进你们的对话窗（你让他画、或他自己想画给你）。你不常用网页时可以关掉——关着他一张都画不了，省钱。一天最多 3 张。
                  </span>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!webDraw}
                  aria-label="他会在网页给你画"
                  disabled={webDraw == null}
                  className={'settings-switch' + (webDraw ? ' on' : '')}
                  onClick={toggleWebDraw}
                >
                  <span className="settings-switch-knob" />
                </button>
              </div>
              {/* 他会在网页给你寄明信片（2026-08-31）：网页寄「云旅行明信片」硬开关 */}
              <div className="settings-row">
                <span className="settings-row-text">
                  <span className="settings-row-label">他会在网页给你寄明信片</span>
                  <span className="settings-row-desc">
                    开着时，他能现编一趟「云旅行」做成明信片直接寄进你们的对话窗（你说想去、或他自己想带你去）。点开就是全屏翻卡，还带他挑的歌。你不常用网页时可以关掉——关着他一趟都寄不了，省钱。一天最多 3 趟。
                  </span>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!webJourney}
                  aria-label="他会在网页给你寄明信片"
                  disabled={webJourney == null}
                  className={'settings-switch' + (webJourney ? ' on' : '')}
                  onClick={toggleWebJourney}
                >
                  <span className="settings-switch-knob" />
                </button>
              </div>
              {/* 他会给你留歌（2026-09-02）：音乐盒「他给你留歌」硬开关 */}
              <div className="settings-row">
                <span className="settings-row-text">
                  <span className="settings-row-label">他会给你留歌</span>
                  <span className="settings-row-desc">
                    开着时，他想起你可以往你的「音乐盒」里悄悄留一首歌 + 一句话，你打开就看到、点一下整首放。一天最多 3 首。关着他一首都留不了。
                  </span>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!leaveSong}
                  aria-label="他会给你留歌"
                  disabled={leaveSong == null}
                  className={'settings-switch' + (leaveSong ? ' on' : '')}
                  onClick={toggleLeaveSong}
                >
                  <span className="settings-switch-knob" />
                </button>
              </div>
              {/* 他会给你发语音（2026-10-08）硬开关 */}
              <div className="settings-row">
                <span className="settings-row-text">
                  <span className="settings-row-label">他会给你发语音</span>
                  <span className="settings-row-desc">
                    开着时，他特别想你、情绪很满，或者你说想听他声音、很累需要他陪的时候，会在聊天里给你发一条语音。长按语音能看原文和翻译。一天最多 3 条。关着他一条都发不了。
                  </span>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!voiceOn}
                  aria-label="他会给你发语音"
                  disabled={voiceOn == null}
                  className={'settings-switch' + (voiceOn ? ' on' : '')}
                  onClick={toggleVoice}
                >
                  <span className="settings-switch-knob" />
                </button>
              </div>
              {/* 他会自己收藏·翻出照片（2026-09-03「真正两个人的相册」）硬开关 */}
              <div className="settings-row">
                <span className="settings-row-text">
                  <span className="settings-row-label">他会自己收藏·翻出照片</span>
                  <span className="settings-row-desc">
                    开着时，他在聊天里看到你发的珍贵/好笑的照片，能自己收进「我们的相册」并告诉你一声；聊到那话题时，也会翻出一张老照片发给你。关着他既收不了也翻不了。
                  </span>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!albumWeb}
                  aria-label="他会自己收藏·翻出照片"
                  disabled={albumWeb == null}
                  className={'settings-switch' + (albumWeb ? ' on' : '')}
                  onClick={toggleAlbumWeb}
                >
                  <span className="settings-switch-knob" />
                </button>
              </div>
              {/* 生图模型选择（2026-08-21 第二波）：TG + 网页共用；gpt-image 稳 / Nano Banana 便宜些 */}
              {imgModelOpts.length > 0 && (
                <div className="settings-row settings-row-col">
                  <span className="settings-row-text">
                    <span className="settings-row-label">生图模型</span>
                    <span className="settings-row-desc">
                      他画图时用哪个模型（Telegram 和网页共用这个设置）。选一个就切，下一张就用它。
                    </span>
                  </span>
                  <div className="imgmodel-pills">
                    {imgModelOpts.map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        className={'imgmodel-pill' + (o.id === imgModel ? ' on' : '')}
                        onClick={() => pickImgModel(o.id)}
                        disabled={imgModel == null}
                        title={o.note || ''}
                      >
                        <span className="imgmodel-pill-label">{o.label}</span>
                        {o.note && <span className="imgmodel-pill-note">{o.note}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {FLAGS.map((f) => {
                const on = getFlag(f.id);
                const soon = f.status === 'soon';
                return (
                  <div key={f.id} className={'settings-row' + (soon ? ' is-soon' : '')}>
                    <span className="settings-row-text">
                      <span className="settings-row-label">
                        {f.label}
                        {soon && <span className="settings-soon-tag">敬请期待</span>}
                      </span>
                      <span className="settings-row-desc">{f.desc}</span>
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={on}
                      aria-label={f.label}
                      disabled={soon}
                      className={'settings-switch' + (on ? ' on' : '')}
                      onClick={() => setFlag(f.id, !on)}
                    >
                      <span className="settings-switch-knob" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 安心 · 克克掉线了怎么办（2026-08-10 铃点单）：揣兜里的定心卡 */}
          <KekeOfflineHelp />
        </aside>
      </div>

      {/* 星洲背景 · 位置调整器（全屏浮层，盖过抽屉） */}
      {homeWpAdjust && homeWp.img && (
        <WallpaperAdjust
          img={homeWp.img}
          pos={homeWp.pos}
          dim={homeWp.dim}
          blur={homeWp.blur}
          onSave={(pos) => patchHomeWp({ pos })}
          onClose={() => setHomeWpAdjust(false)}
        />
      )}
    </>
  );
}
